import { useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useSEO, BASE } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = [
  "Question sur un terme ou une technique",
  "Question sur l'achat d'une œuvre",
  "Suggestion de contenu pour le site",
  "Je souhaite devenir partenaire (artisan des métiers d'art)",
  "Autre demande",
];

export default function ContactPage() {
  useSEO(
    "Contact Éditorial & Demande d'Information — Sculpteur Français",
    "Formulaire de contact pour poser vos questions pédagogiques ou échanger sur un projet de sculpture contemporaine.",
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact éditorial — Sculpteur Français",
      url: `${BASE}/contact`,
      inLanguage: "fr-FR",
    }
  );

  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
      toast.success("Votre message a bien été enregistré.", {
        description: "Il sera lu avec attention — réponse dès que possible.",
      });
    } catch {
      toast.error("L'envoi a échoué.", { description: "Vérifiez les champs et réessayez." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-ink/20 bg-paper px-4 py-3.5 text-base font-light text-ink outline-none transition-colors duration-300 placeholder:text-smoke/50 focus:border-bronze";

  return (
    <main data-testid="contact-page" className="bg-paper pb-24 pt-28 sm:pt-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <Reveal y={16}>
            <p className="eyebrow">Contact éditorial</p>
          </Reveal>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl">
            Poser une question, suggérer un sujet
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg font-light leading-relaxed text-smoke">
              Ce site est une ressource éditoriale : il ne vend aucune œuvre.
              Vos questions sur la sculpture contemporaine — définitions,
              techniques, acquisition — nourrissent ses futurs contenus.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-ink/15 bg-cream p-8 lg:p-12">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                  Nom
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Votre nom"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  maxLength={200}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="vous@exemple.fr"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-subject" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                Sujet
              </label>
              <select
                id="contact-subject"
                data-testid="contact-subject-select"
                value={form.subject}
                onChange={set("subject")}
                className={inputCls}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-textarea"
                required
                minLength={10}
                maxLength={4000}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Votre question ou suggestion…"
                className={`${inputCls} resize-y`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="group mt-8 inline-flex items-center gap-3 bg-ink px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze disabled:opacity-50"
            >
              {sending ? "Envoi en cours…" : "Envoyer le message"}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            {sent && (
              <p data-testid="contact-success-message" className="mt-5 border-l-2 border-bronze pl-4 text-sm font-light text-body">
                Message bien reçu — merci pour votre contribution à cette ressource.
              </p>
            )}
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-smoke">
              Vos messages sont enregistrés et lus — la réponse vous parvient directement par email.
            </p>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
